import { useMemo, useState } from 'react';
import api from '../../../API';
import { girderThumbnailUrl } from '../../../utils/girderThumbnailUrl';
import SlidePreviewModal from '../SlidePreviewModal';
import { normalizeFilename } from './itemInfoUtils';
import styles from './ItemMetadataEditor.module.css';

/**
 * @param {object} props
 * @param {{ _id?: string, name?: string }[]} props.projectItems
 * @param {Record<string, string>[]} props.rows
 * @param {{ id: string, label: string, showInViewer: boolean }[]} props.metadataColumns
 * @param {{ trimWhitespace: boolean, caseInsensitive: boolean }} props.matchOpts
 * @param {(nextRows: Record<string, string>[]) => void} props.onRowsChange
 */
export function ItemMetadataEditor({
    projectItems = [],
    rows = [],
    metadataColumns = [],
    matchOpts,
    onRowsChange,
}) {
    const [filter, setFilter] = useState('');
    const [previewItem, setPreviewItem] = useState(null);

    const columnIds = useMemo(
        () => metadataColumns.map((c) => c.id).filter(Boolean),
        [metadataColumns],
    );

    const rowIndexByNormKey = useMemo(() => {
        const m = new Map();
        rows.forEach((r, idx) => {
            const k = normalizeFilename(r?.filename, matchOpts);
            if (k && !m.has(k)) m.set(k, idx);
        });
        return m;
    }, [rows, matchOpts]);

    const itemsFiltered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        const list = Array.isArray(projectItems) ? projectItems : [];
        if (!q) return list;
        return list.filter((it) => String(it?.name ?? '').toLowerCase().includes(q));
    }, [projectItems, filter]);

    const orphanRows = useMemo(() => {
        const itemKeys = new Set(
            (projectItems || []).map((it) => normalizeFilename(it?.name, matchOpts)).filter(Boolean),
        );
        return rows.filter((r) => {
            const k = normalizeFilename(r?.filename, matchOpts);
            return k && !itemKeys.has(k);
        });
    }, [rows, projectItems, matchOpts]);

    function updateCell(itemName, colId, cellValue) {
        const v = cellValue == null ? '' : String(cellValue);
        const ik = normalizeFilename(itemName, matchOpts);
        if (!ik) return;

        const nextRows = rows.map((r) => ({ ...r }));
        let idx = rowIndexByNormKey.get(ik);

        if (idx === undefined) {
            const newRow = { filename: itemName };
            columnIds.forEach((cid) => {
                newRow[cid] = cid === colId ? v : '';
            });
            onRowsChange([...nextRows, newRow]);
            return;
        }

        const base = { ...nextRows[idx], filename: nextRows[idx].filename || itemName };
        base[colId] = v;
        nextRows[idx] = base;
        onRowsChange(nextRows);
    }

    function cellValueForItem(itemName, colId) {
        const ik = normalizeFilename(itemName, matchOpts);
        if (!ik) return '';
        const idx = rowIndexByNormKey.get(ik);
        if (idx === undefined) return '';
        const r = rows[idx];
        return r[colId] == null ? '' : String(r[colId]);
    }

    if (projectItems.length === 0) {
        return (
            <p className={styles.muted}>
                Add slides in Items (or reload) to edit metadata in a per-slide grid.
            </p>
        );
    }

    if (columnIds.length === 0) {
        return (
            <p className={styles.muted}>
                Add at least one metadata column below to edit values per slide.
            </p>
        );
    }

    return (
        <div className={styles.wrap}>
            <div className={styles.toolbar}>
                <label htmlFor="item-metadata-grid-filter" className={styles.toolbarLabel}>
                    Filter slides
                </label>
                <input
                    id="item-metadata-grid-filter"
                    className={styles.filterInput}
                    type="search"
                    placeholder="Filter by item name…"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                />
                <span className={styles.toolbarMeta}>
                    {itemsFiltered.length}
                    /
                    {projectItems.length}
                    {' '}
                    shown
                </span>
            </div>
            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th className={styles.stickyName}>Slide (item name)</th>
                            {columnIds.map((cid) => {
                                const col = metadataColumns.find((c) => c.id === cid);
                                const raw = col?.label;
                                const head = raw != null && String(raw) !== '' ? String(raw) : cid;
                                return (
                                    <th key={cid} title={cid}>{head}</th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {itemsFiltered.map((it) => {
                            const name = String(it?.name ?? '');
                            const iid = it?._id != null ? String(it._id) : '';
                            return (
                                <tr key={String(it?._id ?? name)}>
                                    <td className={styles.stickyName}>
                                        <div className={styles.slideCell}>
                                            {iid ? (
                                                <button
                                                    type="button"
                                                    className={styles.thumbButton}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setPreviewItem({ id: iid, name });
                                                    }}
                                                    aria-label={`Open slide preview for ${name || 'slide'}`}
                                                >
                                                    <img
                                                        src={girderThumbnailUrl(api, iid)}
                                                        alt=""
                                                        className={styles.itemThumbnail}
                                                        onError={(e) => {
                                                            e.currentTarget.style.display = 'none';
                                                        }}
                                                    />
                                                </button>
                                            ) : null}
                                            <span className={styles.itemName} title={name}>
                                                {name || '—'}
                                            </span>
                                            {iid ? (
                                                <a
                                                    href={`/dsa#item/${iid}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={styles.dsaPill}
                                                    title="Open in default Girder UI"
                                                >
                                                    DSA
                                                </a>
                                            ) : null}
                                        </div>
                                    </td>
                                    {columnIds.map((cid) => (
                                        <td key={cid}>
                                            <input
                                                className={styles.cellInput}
                                                type="text"
                                                value={cellValueForItem(name, cid)}
                                                onChange={(e) => updateCell(name, cid, e.target.value)}
                                                aria-label={`${name}: ${cid}`}
                                            />
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {orphanRows.length > 0 && (
                <details className={styles.orphan}>
                    <summary>
                        {orphanRows.length}
                        {' '}
                        metadata row
                        {orphanRows.length === 1 ? '' : 's'}
                        {' '}
                        not matching any project item (from import)
                    </summary>
                    <p className={styles.muted}>
                        These rows stay in your configuration and still match in the viewer if a slide with
                        that name exists later.
                    </p>
                </details>
            )}
            {previewItem ? (
                <SlidePreviewModal
                    itemId={previewItem.id}
                    itemName={previewItem.name}
                    onClose={() => setPreviewItem(null)}
                />
            ) : null}
        </div>
    );
}
